/**
 * Auth context — provides current user state to the React app.
 * Supports magic link login, OAuth (Google/Microsoft), and post-login beta verification.
 */
import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { auth, setAccessToken, getAccessToken } from "./api.ts";
import { supabase } from "./supabase-client.ts";

// Use the current origin for OAuth redirects — works on both app.enereg.com and enereg.fly.dev
const SITE_URL = window.location.origin;

interface AuthUser {
  id: string;
  email: string;
  name: string;
  org_id: string;
  org_name: string;
  role?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  betaRequired: boolean;
  sendMagicLink: (email: string) => Promise<{ error?: string }>;
  oAuthSignIn: (provider: "google" | "azure") => Promise<{ error?: string }>;
  verifyBeta: (code: string) => Promise<{ error?: string }>;
  logOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  betaRequired: false,
  sendMagicLink: async () => ({}),
  oAuthSignIn: async () => ({}),
  verifyBeta: async () => ({}),
  logOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [betaRequired, setBetaRequired] = useState(false);

  async function fetchMe() {
    try {
      const result = await auth.getMe();
      if (result.user) {
        if (result.beta_required) {
          setBetaRequired(true);
          setUser(null); // Don't grant access yet
        } else {
          setBetaRequired(false);
          setUser(result.user);
        }
        return true;
      }
    } catch { /* no session */ }
    return false;
  }

  // Check session on mount
  useEffect(() => {
    async function checkSession() {
      const token = getAccessToken();
      if (token) {
        const found = await fetchMe();
        if (found) { setLoading(false); return; }
        setAccessToken(null);
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setAccessToken(session.access_token);
          await fetchMe();
          setLoading(false);
          return;
        }
      } catch { /* no session */ }

      // Fallback: try cookie-based session (local dev / demo login)
      const cookieSession = await fetchMe();
      if (cookieSession) { setLoading(false); return; }

      setLoading(false);
    }
    checkSession();
  }, []);

  // Listen for auth state changes (OAuth callback, magic link)
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_IN" && session) {
        setAccessToken(session.access_token);
        await fetchMe();
        setLoading(false);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  async function handleSendMagicLink(email: string) {
    try {
      // Demo account: use local API login (demo user only exists in SQLite, not Supabase Auth)
      if (email.toLowerCase() === "demo@enereg.com") {
        const result = await auth.logIn({ email, password: "testpass123" });
        if (result.user) {
          await fetchMe();
        }
        return { directLogin: true } as any;
      }

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: SITE_URL + "/auth/callback" },
      });
      if (error) return { error: error.message };
      return {};
    } catch (e: any) {
      return { error: e.message || "Failed to send magic link" };
    }
  }

  async function handleOAuthSignIn(provider: "google" | "azure") {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: SITE_URL + "/auth/callback",
          scopes: provider === "azure" ? "email profile openid User.Read" : "email profile",
        },
      });
      if (error) return { error: error.message };
      return {};
    } catch (e: any) {
      return { error: e.message || "OAuth sign-in failed" };
    }
  }

  async function handleVerifyBeta(code: string) {
    try {
      await auth.verifyBeta(code);
      // Re-fetch user — should now pass beta check
      const result = await auth.getMe();
      if (result.user && !result.beta_required) {
        setBetaRequired(false);
        setUser(result.user);
        return {};
      }
      return { error: "Verification failed" };
    } catch (e: any) {
      return { error: e.message || "Invalid beta code" };
    }
  }

  async function handleLogOut() {
    await auth.logOut().catch(() => {});
    await supabase.auth.signOut().catch(() => {});
    setAccessToken(null);
    setUser(null);
    setBetaRequired(false);
  }

  return (
    <AuthContext.Provider value={{
      user, loading, betaRequired,
      sendMagicLink: handleSendMagicLink,
      oAuthSignIn: handleOAuthSignIn,
      verifyBeta: handleVerifyBeta,
      logOut: handleLogOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
